import { useState, useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { useCookieConsent } from "@/hooks/useCookieConsent";

export const COOKIE_SETTINGS_EVENT = "ab:open-cookie-settings";

export function CookieBanner() {
  const { hydrated, hasDecided, analyticsEnabled, acceptAll, rejectAll, savePreferences } =
    useCookieConsent();

  const [open, setOpen] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [analyticsToggle, setAnalyticsToggle] = useState(false);

  // Show banner once hydrated and user hasn't decided yet
  useEffect(() => {
    if (hydrated && !hasDecided) setOpen(true);
  }, [hydrated, hasDecided]);

  // Listen for "re-open settings" event dispatched from e.g. the footer
  useEffect(() => {
    function handleOpen() {
      setAnalyticsToggle(analyticsEnabled);
      setShowPanel(true);
      setOpen(true);
    }
    window.addEventListener(COOKIE_SETTINGS_EVENT, handleOpen);
    return () => window.removeEventListener(COOKIE_SETTINGS_EVENT, handleOpen);
  }, [analyticsEnabled]);

  if (!open) return null;

  function handleAcceptAll() {
    acceptAll();
    setOpen(false);
    setShowPanel(false);
  }

  function handleRejectAll() {
    rejectAll();
    setOpen(false);
    setShowPanel(false);
  }

  function handleSavePreferences() {
    savePreferences(analyticsToggle);
    setOpen(false);
    setShowPanel(false);
  }

  function openPanel() {
    setAnalyticsToggle(analyticsEnabled);
    setShowPanel(true);
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6">
      <div className="mx-auto max-w-4xl rounded-xl border border-white/10 bg-[hsl(240,10%,6%)] shadow-2xl">
        {/* Main banner row */}
        <div className="flex flex-col gap-4 p-5 md:flex-row md:items-center md:justify-between">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 text-xl" aria-hidden>🍪</span>
            <div>
              <p className="text-sm font-medium text-white">We use cookies</p>
              <p className="mt-0.5 text-xs text-white/60">
                We use Microsoft Clarity to understand how visitors interact with our site — helping
                us improve your experience.{" "}
                <Link href="/privacy" className="underline underline-offset-2 hover:text-white/90">
                  Privacy Policy
                </Link>
              </p>
            </div>
          </div>

          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {!showPanel && (
              <button
                onClick={openPanel}
                className="text-xs text-white/50 underline underline-offset-2 hover:text-white/80"
              >
                Manage preferences
              </button>
            )}
            <Button variant="outline" size="sm" onClick={handleRejectAll}>
              Necessary only
            </Button>
            <Button size="sm" onClick={handleAcceptAll}>
              Accept all
            </Button>
          </div>
        </div>

        {/* Expandable preferences panel */}
        {showPanel && (
          <div className="border-t border-white/10 px-5 pb-5 pt-4">
            <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-white/40">
              Cookie settings
            </p>
            <div className="space-y-3">
              {/* Necessary */}
              <div className="flex items-start justify-between gap-4 rounded-lg bg-white/[0.03] px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-white">Necessary</p>
                  <p className="mt-0.5 text-xs text-white/50">
                    Required for the site to function. Cannot be disabled.
                  </p>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                  <span className="text-xs text-white/40">Always on</span>
                  <Switch checked disabled />
                </div>
              </div>

              {/* Analytics */}
              <div className="flex items-start justify-between gap-4 rounded-lg bg-white/[0.03] px-4 py-3">
                <div>
                  <p className="text-sm font-medium text-white">Analytics</p>
                  <p className="mt-0.5 text-xs text-white/50">
                    Microsoft Clarity — session recording and heatmaps. Cookie:{" "}
                    <span className="font-mono">_clck</span> (365 days).
                  </p>
                </div>
                <Switch
                  checked={analyticsToggle}
                  onCheckedChange={setAnalyticsToggle}
                />
              </div>
            </div>

            <div className="mt-4 flex justify-end gap-2">
              <Button variant="outline" size="sm" onClick={handleRejectAll}>
                Necessary only
              </Button>
              <Button size="sm" onClick={handleSavePreferences}>
                Save preferences
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** Drop this anywhere (e.g. Footer) to let users re-open cookie settings. */
export function CookieSettingsButton({ className }: { className?: string }) {
  function open() {
    window.dispatchEvent(new Event(COOKIE_SETTINGS_EVENT));
  }
  return (
    <button onClick={open} className={className}>
      Cookie settings
    </button>
  );
}
